import { Config } from 'config/envConfig';
import React, { useEffect, useState } from 'react';
import { Input, Button, Dropdown, NoInteractionView, alert, AlertType } from '@quyva/shared-ui';
import { faSave, faUserPlus } from '@fortawesome/free-solid-svg-icons';
import { OrganizationAccessOptions, isEmailValid } from '@quyva/common';
import { getUrl } from '../../../pdfDisplay/utils';
import { OrganizationMemberListComponent } from '../../../shareRepository/userListComponent';

export const ManageOrganizationMembers = ({
    user,
    setViewOrganizationModal,
}) => {
    const [newUserEmail, setNewUserEmail] = useState('');
    const [memberList, setMemberList] = useState([]);
    const [addedMemberList, setAddedMemberList] = useState([]);
    const [accessRights, setAccessRights] = useState(
        OrganizationAccessOptions.ORGANISATION_USER
    );
    const [enableSaving, setEnableSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [usersToRemove, setUsersToRemove] = useState([]);
    const [skipEmail, setSkipEmail] = useState(false);
    // Read the token fresh from localStorage (where token-refresh writes it) rather
    // than the `user` prop, which can be stale after a refresh and cause 401s.
    const encodedToken = encodeURIComponent(
        JSON.parse(localStorage.getItem('current') || 'null')?.token || user.token
    );

    async function getOrganizationMembers() {
        try {
            setIsLoading(true);
            const response = await fetch(
                getUrl(
                    `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/organization/members`
                ),
                {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: encodedToken,
                    },
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();
            const membersData = result.members;

            // Sort members: admins first, then by email
            membersData.sort((a, b) => {
                if (
                    a.access_rights ===
                        OrganizationAccessOptions.ORGANISATION_ADMIN &&
                    b.access_rights !==
                        OrganizationAccessOptions.ORGANISATION_ADMIN
                )
                    return -1;
                if (
                    a.access_rights !==
                        OrganizationAccessOptions.ORGANISATION_ADMIN &&
                    b.access_rights ===
                        OrganizationAccessOptions.ORGANISATION_ADMIN
                )
                    return 1;
                return a.email.localeCompare(b.email);
            });

            setMemberList(membersData);
            setIsLoading(false);
        } catch (error) {
            alert(AlertType.ERROR, 'Failed to fetch organization members');
            console.error('Error fetching organization members:', error);
            setIsLoading(false);
        }
    }

    useEffect(() => {
        getOrganizationMembers();
    }, []);

    if (isLoading || isSaving) {
        return (
            <NoInteractionView
                viewText={
                    isSaving
                        ? 'Saving organization members...'
                        : 'Loading organization members...'
                }
            />
        );
    }

    const addNewMember = async () => {
        const newEmail = newUserEmail.toLowerCase();

        // Check if the user is already in the list
        const combinedMemberList = [...memberList, ...addedMemberList];
        for (let member of combinedMemberList) {
            if (member.email.toLowerCase() === newEmail) {
                alert(
                    AlertType.ERROR,
                    'User already present in organization members list'
                );
                return;
            }
        }

        if (user.email.toLowerCase() === newEmail) {
            alert(AlertType.ERROR, "Can't add yourself!");
            return;
        }

        // Check if the user is already part of an organization
        const response = await fetch(
            getUrl(
                `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/check-user-organization`
            ),
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: encodedToken,
                },
                body: JSON.stringify({ email: newEmail }),
            }
        );

        if (response.ok) {
            const data = await response.json();
            if (data.isOrgMember) {
                alert(
                    AlertType.ERROR,
                    data.message || 'Failed to check user organization status'
                );
                return;
            }
        }

        const newMemberData = {
            email: newEmail,
            access_rights: accessRights,
        };

        const updatedMemberList = [newMemberData, ...addedMemberList];
        setAddedMemberList(updatedMemberList);
        setNewUserEmail('');
        setEnableSaving(true);
    };

    const dropDownList = Object.entries(OrganizationAccessOptions).map(
        ([key, value], index) => ({
            key: index,
            label: value, // Display the actual access rights ("user" or "admin")
            disabled: false,
            selected: accessRights === value,
            action: () => {
                if (value && accessRights !== value) {
                    setAccessRights(value);
                }
            },
        })
    );

    const updateOrganizationList = async () => {
        try {
            const response = await fetch(
                getUrl(
                    `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/organization/manage-users`
                ),
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: encodedToken,
                    },
                    body: JSON.stringify({
                        users: [...addedMemberList, ...memberList],
                        usersToRemove: usersToRemove,
                        skipEmail: skipEmail,
                    }),
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();
            await getOrganizationMembers();
            setAddedMemberList([]);
            setUsersToRemove([]);
            setEnableSaving(false);

            return result;
        } catch (error) {
            alert(AlertType.ERROR, 'Failed to update organization members');
            console.error('Error updating organization members:', error);
            throw error;
        }
    };

    const saveOrganizationMembers = async () => {
        setIsSaving(true);
        try {
            // Call the function to update the organization member list
            const result = await updateOrganizationList();
            
            // Display results to user
            if (result && result.results) {
                const { added, updated, removed, failed } = result.results;
                
                // Show success messages
                if (added.length > 0) {
                    alert(AlertType.SUCCESS, `Successfully added ${added.length} member(s)`);
                }
                if (updated.length > 0) {
                    alert(AlertType.SUCCESS, `Successfully updated ${updated.length} member(s)`);
                }
                if (removed.length > 0) {
                    alert(AlertType.SUCCESS, `Successfully removed ${removed.length} member(s)`);
                }
                
                // Show failure messages
                if (failed.length > 0) {
                    failed.forEach(failure => {
                        alert(AlertType.ERROR, `${failure.email}: ${failure.reason}`);
                    });
                }
            }
            
            setIsSaving(false);
            setSkipEmail(false); // Reset skipEmail state
            setViewOrganizationModal(false);
        } catch (error) {
            setIsSaving(false);
            alert(AlertType.ERROR, 'Failed to save organization members');
        }
    };

    return (
        <div className='w-[90vw] min-w-[90px] md:w-[60vw] md:min-w-[500px] flex-col p-2 overflow-y-auto'>
            <div className='border-b-2 border-white pt-3 pb-4'>
                <div className='flex justify-center items-center'>
                <Input
                    type={'email'}
                    placeholder={'Email'}
                    value={newUserEmail}
                    className={'mr-3 w-full truncate text-base'}
                    onChange={(e) => setNewUserEmail(e.target.value)}
                />
                <Dropdown
                    dropDownList={dropDownList}
                    showButtonText={true}
                    buttonText={accessRights}
                    buttonHoverText={'Change access rights of user'}
                    textColor={
                        accessRights ===
                        OrganizationAccessOptions.ORGANISATION_ADMIN
                            ? 'backgrounds-settings100'
                            : 'icons-primary'
                    }
                    backgroundColor={
                        accessRights ===
                        OrganizationAccessOptions.ORGANISATION_ADMIN
                            ? 'backgrounds-settings100'
                            : 'icons-primary'
                    }
                    buttonClassName={'mr-3 ml-3 w-24'}
                    listClassName={''}
                    disabled={false}
                />
                <Button
                    className=''
                    text={'Add'}
                    onClick={(event) => {
                        addNewMember();
                        event.stopPropagation();
                    }}
                    disabled={!isEmailValid(newUserEmail)}
                    hoverText={'Add new member to organization'}
                    icon={faUserPlus}
                    type={'success'}
                />
                </div>
                <div className='flex items-center justify-start mt-3 px-2'>
                    <input
                        type='checkbox'
                        id='skipEmail'
                        checked={skipEmail}
                        onChange={(e) => setSkipEmail(e.target.checked)}
                        className='mr-2 w-4 h-4 cursor-pointer'
                    />
                    <label
                        htmlFor='skipEmail'
                        className='text-sm text-texts-secondary cursor-pointer select-none'
                    >
                        Add members directly without sending email invitation
                    </label>
                </div>
            </div>

            {addedMemberList && addedMemberList.length !== 0 && (
                <div className='border-b-2 border-white'>
                    <div className='flex justify-between items-center font-semibold mt-2 px-2 py-2 text-texts-secondary300'>
                        <div>New Members</div>
                        <div className='mr-2'>Actions</div>
                    </div>

                    <div className='max-h-48 overflow-auto w-full'>
                        <OrganizationMemberListComponent
                            memberList={addedMemberList}
                            stateAction={setAddedMemberList}
                            enableSaving={enableSaving}
                            setEnableSaving={setEnableSaving}
                        />
                    </div>
                </div>
            )}

            <div>
                <div className='flex justify-between items-center font-semibold mt-2 px-2 py-2 text-texts-secondary300'>
                    <div>Existing Members</div>
                    <div className='mr-2'>Actions</div>
                </div>

                <div className='max-h-52 overflow-auto w-full'>
                    <OrganizationMemberListComponent
                        memberList={memberList}
                        stateAction={setMemberList}
                        enableSaving={enableSaving}
                        setEnableSaving={setEnableSaving}
                        setUsersToRemove={setUsersToRemove}
                        currentUserEmail={user.email}
                    />
                </div>
            </div>

            <div className='flex border-t-2 border-white pt-2 justify-end items-center'>
                <Button
                    className='whitespace-nowrap overflow-hidden text-ellipsis w-32'
                    text={addedMemberList.length ? 'Save' : 'Update'}
                    onClick={async (event) => {
                        await saveOrganizationMembers();
                        event.stopPropagation();
                    }}
                    hoverText={'Save organization members'}
                    icon={faSave}
                    type={'success'}
                    disabled={!enableSaving}
                />
            </div>
        </div>
    );
};
